/**
 * Report Builder Page
 *
 * A flexible report builder for Monitor domain data from Convex.
 * Uses the reusable ConvexReportBuilder component.
 */

import { useEffect, useMemo } from 'react';
import { usePaginatedQuery, useQuery } from 'convex/react';
import { format } from 'date-fns';
import {
  Sparkles,
  RotateCw,
  TrendingDown,
  ArrowRightLeft,
  ShieldCheck,
  BadgeCheck,
  BadgeX,
  Globe,
  Trash2,
} from 'lucide-react';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { ConvexReportBuilder } from '@/components/report-builder';
import type { FilterField, ReportColumn, FilterPreset } from '@/components/report-builder';
import { createCondition } from '@/components/report-builder/filter-utils';
import { useReportBuilderContactEmails } from '@/hooks/use-contacts';
import { getRegion, getRegionLabel, REGIONS } from '@/lib/region-utils';
import type { Region } from '@/lib/region-utils';
import { api } from '../../../../convex/_generated/api';

// ============================================================================
// Types
// ============================================================================

interface MonitorDomainRow {
  [key: string]: unknown;
  domain: string;
  tld: string;
  status: string;
  lastScanStatus: string;
  state: string | null;
  country: string | null;
  region: Region | null;
  registrationDate: number | null;
  expiryDate: number | null;
  liveSince: number | null;
  launchVelocityDays: number | null;
  hasActiveOverride: boolean;
  isMonitored: boolean;
  inCurrentZoneFile: boolean;
  cybersecurityBadgeStatus: string | null;
  hasuraOrgName: string | null;
  registrantEmail: string | null;
  registrantAltEmail: string | null;
  verificationEmail: string | null;
  verificationAltEmail: string | null;
  rddsName: string | null;
  registrantAltName: string | null;
  verificationName: string | null;
  verificationAltName: string | null;
  registrantCity: string | null;
  lastScanAt: number | null;
  registrar: string | null;
  useRestriction: string | null;
  // Normalized value used for enum quick filters (null/blank -> '')
  useRestrictionFilterValue: string;
  useRestrictionNote: string | null;
  useRestrictionNotePresent: boolean;
  // Event-related fields (last 30 days)
  hasRecentEvent: boolean;
  lastEventAt: number | null;
  lastEventType: string | null;
  eventCount: number;
  hasNewLiveEvent: boolean;
  hasPreviousLiveEvent: boolean;
  hasChangeLiveEvent: boolean;
  hasRedirectEvent: boolean;
  hasOverrideEvent: boolean;
  hasBadgeDetectedEvent: boolean;
  hasBadgeRemovedEvent: boolean;
  hasNewDomainEvent: boolean;
  hasDomainRemovedEvent: boolean;
  // FDIC-related fields
  hasFdic: boolean;
  fdicCert: string | null;
  fdicInstitution: string | null;
  fdicStatus: string | null;
  fdicAssetSize: number | null;
  hasFdicIssue: boolean;
  fdicCertCount: number;
}

// ============================================================================
// Configuration
// ============================================================================

// Column definitions for the report grid
const MONITOR_COLUMNS: ReportColumn<MonitorDomainRow>[] = [
  {
    field: 'domain',
    headerName: 'Domain',
    minWidth: 180,
    flex: 1,
  },
  {
    field: 'tld',
    headerName: 'TLD',
    minWidth: 70,
    valueFormatter: (value) => value ? `.${String(value).charAt(0).toUpperCase()}${String(value).slice(1)}` : '—',
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 150,
    valueFormatter: (value) => String(value || '').replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase()),
  },
  {
    field: 'hasuraOrgName',
    headerName: 'Organization',
    minWidth: 200,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'rddsName',
    headerName: 'RDDS Name',
    minWidth: 220,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'registrantAltName',
    headerName: 'Registrant Alt Name',
    minWidth: 220,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'registrantCity',
    headerName: 'Registrant City',
    minWidth: 160,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'registrantEmail',
    headerName: 'Registrant Email',
    minWidth: 240,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'registrantAltEmail',
    headerName: 'Registrant Alt Email',
    minWidth: 240,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'verificationName',
    headerName: 'Verification Name',
    minWidth: 220,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'verificationAltName',
    headerName: 'Verification Alt Name',
    minWidth: 220,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'verificationEmail',
    headerName: 'Verification Email',
    minWidth: 240,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'verificationAltEmail',
    headerName: 'Verification Alt Email',
    minWidth: 240,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'registrar',
    headerName: 'Registrar',
    minWidth: 180,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'useRestriction',
    headerName: 'Use Restriction',
    minWidth: 180,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'useRestrictionNote',
    headerName: 'Use Restriction Note',
    minWidth: 240,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'state',
    headerName: 'State',
    minWidth: 80,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'country',
    headerName: 'Country',
    minWidth: 100,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'region',
    headerName: 'Region',
    minWidth: 140,
    valueFormatter: (value) => getRegionLabel(value as Region | null),
  },
  {
    field: 'registrationDate',
    headerName: 'Reg. Date',
    minWidth: 120,
    valueFormatter: (value) => value ? format(new Date(value as number), 'MMM d, yyyy') : '—',
  },
  {
    field: 'expiryDate',
    headerName: 'Expiry Date',
    minWidth: 120,
    valueFormatter: (value) => value ? format(new Date(value as number), 'MMM d, yyyy') : '—',
  },
  {
    field: 'liveSince',
    headerName: 'Live Since',
    minWidth: 120,
    valueFormatter: (value) => value ? format(new Date(value as number), 'MMM d, yyyy') : '—',
  },
  {
    field: 'launchVelocityDays',
    headerName: 'Velocity',
    minWidth: 100,
    valueFormatter: (value) => {
      if (value === null || value === undefined)
        return '—';
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? `${n}d` : '—';
    },
  },
  {
    field: 'lastScanAt',
    headerName: 'Last Scan',
    minWidth: 150,
    valueFormatter: (value) => value ? format(new Date(value as number), 'MMM d, h:mm a') : '—',
  },
  {
    field: 'hasActiveOverride',
    headerName: 'Override',
    minWidth: 100,
    valueFormatter: (value) => value ? 'Yes' : 'No',
  },
  {
    field: 'cybersecurityBadgeStatus',
    headerName: 'Badge',
    minWidth: 120,
    valueFormatter: (value) => {
      if (!value)
        return '—';
      return String(value).replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());
    },
  },
  {
    field: 'hasRecentEvent',
    headerName: 'Event',
    minWidth: 90,
    valueFormatter: (value) => value ? 'Yes' : 'No',
  },
  {
    field: 'lastEventType',
    headerName: 'Last Event',
    minWidth: 140,
    valueFormatter: (value) => {
      if (!value)
        return '—';
      return String(value).replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());
    },
  },
  {
    field: 'lastEventAt',
    headerName: 'Event Date',
    minWidth: 130,
    valueFormatter: (value) => value ? format(new Date(value as number), 'MMM d, yyyy') : '—',
  },
  {
    field: 'eventCount',
    headerName: 'Events',
    minWidth: 80,
    valueFormatter: (value) => value ? String(value) : '0',
  },
  // FDIC columns
  {
    field: 'fdicCert',
    headerName: 'FDIC Cert',
    minWidth: 100,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'fdicInstitution',
    headerName: 'FDIC Institution',
    minWidth: 200,
    flex: 1,
    valueFormatter: (value) => value ? String(value) : '—',
  },
  {
    field: 'fdicStatus',
    headerName: 'FDIC Status',
    minWidth: 110,
    valueFormatter: (value) => {
      if (!value)
        return '—';
      return String(value).replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());
    },
  },
  {
    field: 'fdicAssetSize',
    headerName: 'FDIC Asset Size',
    minWidth: 150,
    valueFormatter: (value) => {
      if (value === null || value === undefined)
        return '—';
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n))
        return '—';
      // FDIC API returns asset sizes in thousands of dollars
      return (n * 1000).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      });
    },
  },
];

// Filter field definitions
const BASE_MONITOR_FILTER_FIELDS: FilterField<MonitorDomainRow>[] = [
  {
    field: 'tld',
    label: 'TLD',
    groupLabel: 'Zone',
    type: 'enum',
    multiple: true,
    enumOptions: [
      { value: 'bank', label: '.Bank' },
      { value: 'insurance', label: '.Insurance' },
    ],
  },
  {
    field: 'status',
    label: 'Status',
    groupLabel: 'Status',
    type: 'enum',
    multiple: true,
    enumOptions: [
      { value: 'live', label: 'Live' },
      { value: 'parked', label: 'Parked' },
      { value: 'not_in_zone', label: 'Not in Zone' },
      { value: 'redirect_in_zone', label: 'Redirect (In Zone)' },
      { value: 'redirect_out_of_zone', label: 'Redirect (Out)' },
      { value: 'error', label: 'Error' },
    ],
  },
  {
    field: 'hasActiveOverride',
    label: 'Override',
    groupLabel: 'Override',
    type: 'enum',
    enumOptions: [
      { value: 'true', label: 'With Override' },
      { value: 'false', label: 'No Override' },
    ],
  },
  {
    field: 'cybersecurityBadgeStatus',
    label: 'Badge Status',
    type: 'enum',
    enumOptions: [
      { value: 'present', label: 'Present' },
      { value: 'not_present', label: 'Not Present' },
      { value: 'no_longer_present', label: 'Removed' },
      { value: 'manual_add', label: 'Manual Add' },
    ],
  },
  {
    field: 'domain',
    label: 'Domain',
    type: 'string',
  },
  {
    field: 'hasuraOrgName',
    label: 'Organization',
    type: 'string',
  },
  {
    field: 'rddsName',
    label: 'RDDS Name',
    type: 'string',
  },
  {
    field: 'registrantAltName',
    label: 'Registrant Alt Name',
    type: 'string',
  },
  {
    field: 'registrantCity',
    label: 'Registrant City',
    type: 'string',
  },
  {
    field: 'registrantEmail',
    label: 'Registrant Email',
    type: 'string',
  },
  {
    field: 'registrantAltEmail',
    label: 'Registrant Alt Email',
    type: 'string',
  },
  {
    field: 'verificationName',
    label: 'Verification Name',
    type: 'string',
  },
  {
    field: 'verificationAltName',
    label: 'Verification Alt Name',
    type: 'string',
  },
  {
    field: 'verificationEmail',
    label: 'Verification Email',
    type: 'string',
  },
  {
    field: 'verificationAltEmail',
    label: 'Verification Alt Email',
    type: 'string',
  },
  {
    field: 'registrar',
    label: 'Registrar',
    groupLabel: 'Registrar',
    type: 'enum',
    multiple: true,
    enumOptions: [
      { value: 'EnCirca, Inc.', label: 'EnCirca' },
      { value: '101domain GRS Limited', label: '101domain' },
      { value: 'Lexsynergy Limited', label: 'Lexsynergy' },
      { value: 'MarkMonitor, Inc.', label: 'MarkMonitor' },
      { value: 'CSC Corporate Domains, Inc.', label: 'CSC Corporate Domains' },
      { value: 'Safenames Ltd', label: 'Safenames' },
      { value: 'Key-Systems LLC', label: 'Key-Systems' },
      { value: 'Gandi SAS', label: 'Gandi' },
      { value: 'Ascio Technologies, Inc. Danmark - Filial af Ascio technologies, Inc. USA', label: 'Ascio Technologies' },
      { value: '007 Names, Inc.', label: '007 Names' },
      { value: 'EuroDNS SA', label: 'EuroDNS' },
      { value: 'Hosting Concepts B.V. d/b/a Registrar.eu', label: 'Registrar.eu' },
      { value: 'NameWeb BVBA', label: 'NameWeb' },
      { value: 'Nom-iq Ltd. dba COM LAUDE', label: 'COM LAUDE' },
      { value: 'fTLD Registry Services, LLC', label: 'fTLD Registry Services' },
      { value: 'Regional Network Information Center, JSC dba RU-CENTER', label: 'RU-CENTER' },
      { value: 'InternetX GmbH', label: 'InternetX' },
      { value: 'Beijing Guoxu Network Technology Co., Ltd.', label: 'Beijing Guoxu' },
      { value: 'Global Village GmbH', label: 'Global Village' },
      { value: 'Instra Corporation Pty Ltd', label: 'Instra Corporation' },
      { value: 'Nameshield SAS', label: 'Nameshield' },
      { value: 'OwnRegistrar, Inc.', label: 'OwnRegistrar' },
      { value: 'Entorno Digital, S.A.', label: 'Entorno Digital' },
      { value: 'Net-Chinese Co., Ltd.', label: 'Net-Chinese' },
      { value: 'INWX GmbH', label: 'INWX' },
      { value: 'Domain The Net Technologies, Ltd.', label: 'Domain The Net' },
      { value: 'Variomedia AG', label: 'Variomedia' },
      { value: 'Secura GmbH', label: 'Secura' },
    ],
  },
  {
    field: 'state',
    label: 'State',
    type: 'string',
  },
  {
    field: 'country',
    label: 'Country',
    type: 'string',
  },
  {
    field: 'region',
    label: 'Region',
    groupLabel: 'Geography',
    type: 'enum',
    multiple: true,
    enumOptions: REGIONS.map((r) => ({ value: r.value, label: r.label })),
  },
  {
    field: 'launchVelocityDays',
    label: 'Launch Velocity',
    type: 'number',
  },
  {
    field: 'liveSince',
    label: 'Live Since',
    type: 'date',
  },
  {
    field: 'registrationDate',
    label: 'Registration Date',
    type: 'date',
  },
  {
    field: 'expiryDate',
    label: 'Expiry Date',
    type: 'date',
  },
  // Event-based filters
  {
    field: 'lastEventType',
    label: 'Last Event Type',
    groupLabel: 'Event Type (30d)',
    type: 'enum',
    multiple: true,
    enumOptions: [
      { value: 'new_live', label: 'New Live', icon: Sparkles },
      { value: 'previous_live', label: 'Returned Live', icon: RotateCw },
      { value: 'change_live', label: 'Left Live', icon: TrendingDown },
      { value: 'redirect', label: 'Redirect', icon: ArrowRightLeft },
      { value: 'override', label: 'Override', icon: ShieldCheck },
      { value: 'badge_detected', label: 'Badge Found', icon: BadgeCheck },
      { value: 'badge_removed', label: 'Badge Gone', icon: BadgeX },
      { value: 'new_domain', label: 'New Domain', icon: Globe },
      { value: 'domain_removed', label: 'Removed', icon: Trash2 },
    ],
  },
  {
    field: 'lastEventAt',
    label: 'Last Event Date',
    groupLabel: 'Events',
    type: 'date',
  },
  {
    field: 'eventCount',
    label: 'Event Count',
    groupLabel: 'Events',
    type: 'number',
  },
  // FDIC filters
  {
    field: 'hasFdic',
    label: 'Has FDIC',
    groupLabel: 'FDIC',
    type: 'enum',
    enumOptions: [
      { value: 'true', label: 'With FDIC Cert' },
      { value: 'false', label: 'No FDIC Cert' },
    ],
  },
  {
    field: 'fdicStatus',
    label: 'FDIC Status',
    groupLabel: 'FDIC',
    type: 'enum',
    multiple: true,
    enumOptions: [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'not_found', label: 'Not Found' },
      { value: 'invalid', label: 'Invalid' },
      { value: 'error', label: 'Error' },
    ],
  },
  {
    field: 'hasFdicIssue',
    label: 'FDIC Issue',
    groupLabel: 'FDIC',
    type: 'enum',
    enumOptions: [
      { value: 'true', label: 'Has Issue' },
      { value: 'false', label: 'No Issue' },
    ],
  },
  {
    field: 'fdicCert',
    label: 'FDIC Cert #',
    type: 'string',
  },
  {
    field: 'fdicInstitution',
    label: 'FDIC Institution',
    type: 'string',
  },
];

// Preset filter configurations
const MONITOR_PRESETS: FilterPreset[] = [
  {
    id: 'live-bank',
    name: 'Live .Bank Domains',
    description: 'All live .bank domains',
    filters: {
      id: 'preset-live-bank',
      logic: 'and',
      conditions: [
        createCondition('tld', 'in', ['bank']),
        createCondition('status', 'in', ['live']),
      ],
    },
  },
  {
    id: 'live-insurance',
    name: 'Live .Insurance Domains',
    description: 'All live .insurance domains',
    filters: {
      id: 'preset-live-insurance',
      logic: 'and',
      conditions: [
        createCondition('tld', 'in', ['insurance']),
        createCondition('status', 'in', ['live']),
      ],
    },
  },
  {
    id: 'with-badge',
    name: 'Cybersecurity Badge',
    description: 'Domains with cybersecurity badge detected',
    filters: {
      id: 'preset-with-badge',
      logic: 'and',
      conditions: [
        createCondition('cybersecurityBadgeStatus', 'in', ['present', 'manual_add']),
      ],
    },
  },
  {
    id: 'with-override',
    name: 'With Overrides',
    description: 'Domains with active status override',
    filters: {
      id: 'preset-override',
      logic: 'and',
      conditions: [
        createCondition('hasActiveOverride', 'equals', true),
      ],
    },
  },
  {
    id: 'errors',
    name: 'Scan Errors',
    description: 'Domains with scan errors',
    filters: {
      id: 'preset-errors',
      logic: 'and',
      conditions: [
        createCondition('status', 'in', ['error']),
      ],
    },
  },
  {
    id: 'out-of-zone-redirects',
    name: 'Out-of-Zone Redirects',
    description: 'Domains redirecting outside .bank/.insurance',
    filters: {
      id: 'preset-out-zone',
      logic: 'and',
      conditions: [
        createCondition('status', 'in', ['redirect_out_of_zone']),
      ],
    },
  },
  {
    id: 'fast-launchers',
    name: 'Fast Launchers (< 90 days)',
    description: 'Domains that went live within 90 days of registration',
    filters: {
      id: 'preset-fast',
      logic: 'and',
      conditions: [
        createCondition('status', 'in', ['live']),
        createCondition('launchVelocityDays', 'lte', 90),
      ],
    },
  },
  {
    id: 'recent-live',
    name: 'Recently Went Live',
    description: 'Domains that went live in the last 30 days',
    filters: {
      id: 'preset-recent',
      logic: 'and',
      conditions: [
        createCondition('status', 'in', ['live']),
        createCondition('liveSince', 'gte', Date.now() - 30 * 24 * 60 * 60 * 1000),
      ],
    },
  },
  {
    id: 'us-domains',
    name: 'US & Territories',
    description: 'Domains in US and US territories',
    filters: {
      id: 'preset-us',
      logic: 'and',
      conditions: [
        createCondition('region', 'in', ['US']),
      ],
    },
  },
  {
    id: 'eu-eea-domains',
    name: 'EU / EEA Domains',
    description: 'Domains in the European Union or European Economic Area',
    filters: {
      id: 'preset-eu',
      logic: 'and',
      conditions: [
        createCondition('region', 'in', ['EU-EEA']),
      ],
    },
  },
  {
    id: 'international-domains',
    name: 'International (Non-US)',
    description: 'All domains outside the US',
    filters: {
      id: 'preset-int',
      logic: 'and',
      conditions: [
        createCondition('region', 'in', ['EU-EEA', 'ASIA', 'OTHER']),
      ],
    },
  },
  {
    id: 'asia-domains',
    name: 'Asia',
    description: 'Domains in the Asia-Pacific region',
    filters: {
      id: 'preset-asia',
      logic: 'and',
      conditions: [
        createCondition('region', 'in', ['ASIA']),
      ],
    },
  },
  // Event-based presets (use boolean fields that exist in row data)
  {
    id: 'new-live-events',
    name: 'Recently Went Live (Events)',
    description: 'Domains with new_live events in the last 30 days',
    filters: {
      id: 'preset-new-live-events',
      logic: 'and',
      conditions: [
        createCondition('hasNewLiveEvent', 'equals', true),
      ],
    },
  },
  {
    id: 'status-changes',
    name: 'Recent Status Changes',
    description: 'Domains with change_live events in the last 30 days',
    filters: {
      id: 'preset-status-changes',
      logic: 'and',
      conditions: [
        createCondition('hasChangeLiveEvent', 'equals', true),
      ],
    },
  },
  {
    id: 'badge-changes',
    name: 'Badge Changes',
    description: 'Domains with badge detected or removed events',
    filters: {
      id: 'preset-badge-changes',
      logic: 'or',
      conditions: [
        createCondition('hasBadgeDetectedEvent', 'equals', true),
        createCondition('hasBadgeRemovedEvent', 'equals', true),
      ],
    },
  },
  {
    id: 'recent-redirects',
    name: 'Recent Redirect Changes',
    description: 'Domains with redirect events in the last 30 days',
    filters: {
      id: 'preset-recent-redirects',
      logic: 'and',
      conditions: [
        createCondition('hasRedirectEvent', 'equals', true),
      ],
    },
  },
  {
    id: 'high-activity',
    name: 'High Activity (5+ events)',
    description: 'Domains with 5 or more events in the last 30 days',
    filters: {
      id: 'preset-high-activity',
      logic: 'and',
      conditions: [
        createCondition('eventCount', 'gte', 5),
      ],
    },
  },
  {
    id: 'any-recent-events',
    name: 'Any Recent Events',
    description: 'All domains with events in the last 30 days',
    filters: {
      id: 'preset-any-events',
      logic: 'and',
      conditions: [
        createCondition('eventCount', 'gte', 1),
      ],
    },
  },
  {
    id: 'new-domains-added',
    name: 'New Domains Added',
    description: 'Domains added to zone file in the last 30 days',
    filters: {
      id: 'preset-new-domains',
      logic: 'and',
      conditions: [
        createCondition('hasNewDomainEvent', 'equals', true),
      ],
    },
  },
  // FDIC presets
  {
    id: 'fdic-issues',
    name: 'FDIC Issues',
    description: 'Domains with non-active FDIC certificates',
    filters: {
      id: 'preset-fdic-issues',
      logic: 'and',
      conditions: [
        createCondition('hasFdicIssue', 'equals', true),
      ],
    },
  },
  {
    id: 'fdic-inactive',
    name: 'FDIC Inactive',
    description: 'Domains linked to inactive FDIC institutions',
    filters: {
      id: 'preset-fdic-inactive',
      logic: 'and',
      conditions: [
        createCondition('fdicStatus', 'in', ['inactive']),
      ],
    },
  },
  {
    id: 'with-fdic',
    name: 'With FDIC Cert',
    description: 'Domains that have an FDIC certificate on file',
    filters: {
      id: 'preset-with-fdic',
      logic: 'and',
      conditions: [
        createCondition('hasFdic', 'equals', true),
      ],
    },
  },
  {
    id: 'no-fdic',
    name: 'No FDIC Cert',
    description: 'Domains without an FDIC certificate',
    filters: {
      id: 'preset-no-fdic',
      logic: 'and',
      conditions: [
        createCondition('hasFdic', 'equals', false),
      ],
    },
  },
];

// ============================================================================
// Main Component
// ============================================================================

function ReportBuilderPage() {
  const { setLabel, removeLabel } = useBreadcrumb();

  // Set breadcrumb
  useEffect(() => {
    const path = '/app/reports/report-builder';
    setLabel(path, { name: 'Report Builder', path });
    return () => removeLabel(path);
  }, [setLabel, removeLabel]);

  // Domain universe: "not deleted" domains (RDDS seen within ~3 days), including not-in-zone.
  // Paginated to avoid production transport/size caps when returning thousands of rows.
  const cutoffMs = useMemo(() => Date.now() - 3 * 24 * 60 * 60 * 1000, []);
  const {
    results: domainStatuses,
    status: domainStatusesLoadStatus,
    loadMore: loadMoreDomainStatuses,
  } = usePaginatedQuery(
    api.monitor.getActiveDomainStatusesPaginated,
    { cutoffMs },
    { initialNumItems: 1000 },
  );

  // Auto-load all pages (report builder needs the full dataset for client-side filters).
  useEffect(() => {
    if (domainStatusesLoadStatus === 'CanLoadMore') {
      loadMoreDomainStatuses(1000);
    }
  }, [domainStatusesLoadStatus, loadMoreDomainStatuses]);
  const overrides = useQuery(api.monitor.getAllActiveOverrides);
  const reportBuilderDomains = useMemo(
    () => [...new Set((domainStatuses || []).map((d) => d.domain.toLowerCase()))].sort((a, b) => a.localeCompare(b)),
    [domainStatuses],
  );
  const shouldLoadContactEmails = domainStatusesLoadStatus === 'Exhausted' && reportBuilderDomains.length > 0;
  const { data: contactEmailsByDomain } = useReportBuilderContactEmails(
    reportBuilderDomains,
    shouldLoadContactEmails,
  );
  // Fetch events aggregated by domain (last 30 days)
  const eventsByDomain = useQuery(api.monitor.getEventsByDomain, { daysBack: 30 });
  // Fetch FDIC data aggregated by domain
  const fdicByDomainRaw = useQuery(api.fdic.getFdicByDomain);

  // Build override map
  const overrideMap = useMemo(() => {
    const map = new Map<string, string>();
    if (overrides) {
      for (const o of overrides) {
        map.set(o.domain, o.statusOverride);
      }
    }
    return map;
  }, [overrides]);

  // Build FDIC lookup map from array
  const fdicByDomain = useMemo(() => {
    if (!fdicByDomainRaw)
      return null;
    const map = new Map<string, typeof fdicByDomainRaw[number]>();
    for (const item of fdicByDomainRaw) {
      map.set(item.domain, item);
    }
    return map;
  }, [fdicByDomainRaw]);

  const contactEmailMap = useMemo(() => {
    const map = new Map<
      string,
      {
        registrantEmail: string | null;
        registrantAltEmail: string | null;
        verificationEmail: string | null;
        verificationAltEmail: string | null;
        rddsName: string | null;
        registrantAltName: string | null;
        verificationName: string | null;
        verificationAltName: string | null;
        registrantCity: string | null;
        registrantState: string | null;
        registrantCountry: string | null;
      }
    >();
    if (!contactEmailsByDomain)
      return map;
    for (const row of contactEmailsByDomain) {
      map.set(row.domain.toLowerCase(), {
        registrantEmail: row.registrantEmail,
        registrantAltEmail: row.registrantAltEmail,
        verificationEmail: row.verificationEmail,
        verificationAltEmail: row.verificationAltEmail,
        rddsName: row.rddsName,
        registrantAltName: row.registrantAltName,
        verificationName: row.verificationName,
        verificationAltName: row.verificationAltName,
        registrantCity: row.registrantCity,
        registrantState: row.registrantState,
        registrantCountry: row.registrantCountry,
      });
    }
    return map;
  }, [contactEmailsByDomain]);

  // Transform data for the report builder
  const reportData = useMemo((): MonitorDomainRow[] => {
    if (!domainStatuses)
      return [];

    const now = Date.now();

    return domainStatuses.map((d) => {
      const dAny = d as unknown as { useRestriction?: string | null; useRestrictionNote?: string | null };
      const tld = d.domain.split('.').pop() || '';
      const hasActiveOverride = overrideMap.has(d.domain);
      const overrideStatus = overrideMap.get(d.domain);
      const contactEmails = contactEmailMap.get(d.domain.toLowerCase());

      // Compute effective status
      let effectiveStatus: string = d.currentStatus;

      // Not in zone is a separate concept from scan status.
      if (!d.inCurrentZoneFile) {
        effectiveStatus = 'not_in_zone';
      } else if (hasActiveOverride && overrideStatus) {
        effectiveStatus = overrideStatus;
      } else if (d.graceExpiresAt && d.graceExpiresAt > now && d.highWaterStatus) {
        effectiveStatus = d.highWaterStatus;
      }

      // Get event data for this domain
      const domainEvents = eventsByDomain?.[d.domain];

      return {
        domain: d.domain,
        tld,
        status: effectiveStatus,
        lastScanStatus: d.lastScanStatus,
        state: contactEmails?.registrantState ?? null,
        country: contactEmails?.registrantCountry ?? null,
        region: getRegion(contactEmails?.registrantCountry ?? null),
        registrationDate: d.registrationDate,
        expiryDate: d.expiryDate ?? null,
        liveSince: d.liveSince ?? null,
        launchVelocityDays: d.launchVelocityDays
          ?? (d.liveSince && d.registrationDate
            ? Math.floor((d.liveSince - d.registrationDate) / (1000 * 60 * 60 * 24))
            : null),
        hasActiveOverride,
        isMonitored: d.isMonitored,
        inCurrentZoneFile: d.inCurrentZoneFile,
        cybersecurityBadgeStatus: d.cybersecurityBadgeStatus ?? null,
        hasuraOrgName: d.hasuraOrgName ?? null,
        registrantEmail: contactEmails?.registrantEmail ?? null,
        registrantAltEmail: contactEmails?.registrantAltEmail ?? null,
        verificationEmail: contactEmails?.verificationEmail ?? null,
        verificationAltEmail: contactEmails?.verificationAltEmail ?? null,
        rddsName: contactEmails?.rddsName ?? null,
        registrantAltName: contactEmails?.registrantAltName ?? null,
        verificationName: contactEmails?.verificationName ?? null,
        verificationAltName: contactEmails?.verificationAltName ?? null,
        registrantCity: contactEmails?.registrantCity ?? null,
        lastScanAt: d.lastScanAt ?? null,
        // Event-related fields
        hasRecentEvent: !!domainEvents,
        lastEventAt: domainEvents?.lastEventAt ?? null,
        lastEventType: domainEvents?.lastEventType ?? null,
        eventCount: domainEvents?.eventCount ?? 0,
        hasNewLiveEvent: domainEvents?.hasNewLive ?? false,
        hasPreviousLiveEvent: domainEvents?.hasPreviousLive ?? false,
        hasChangeLiveEvent: domainEvents?.hasChangeLive ?? false,
        hasRedirectEvent: domainEvents?.hasRedirect ?? false,
        hasOverrideEvent: domainEvents?.hasOverride ?? false,
        hasBadgeDetectedEvent: domainEvents?.hasBadgeDetected ?? false,
        hasBadgeRemovedEvent: domainEvents?.hasBadgeRemoved ?? false,
        hasNewDomainEvent: domainEvents?.hasNewDomain ?? false,
        hasDomainRemovedEvent: domainEvents?.hasDomainRemoved ?? false,
        // Registrar (from cached Hasura data)
        registrar: d.registrar ?? null,
        // Use restriction fields (from Hasura ftld_domain)
        useRestriction: dAny.useRestriction ?? null,
        useRestrictionFilterValue: (dAny.useRestriction ?? '').trim(),
        useRestrictionNote: dAny.useRestrictionNote ?? null,
        useRestrictionNotePresent: !!(dAny.useRestrictionNote && String(dAny.useRestrictionNote).trim()),
        // FDIC-related fields
        hasFdic: fdicByDomain?.has(d.domain) ?? false,
        fdicCert: fdicByDomain?.get(d.domain)?.primaryCert ?? null,
        fdicInstitution: fdicByDomain?.get(d.domain)?.primaryInstitution ?? null,
        fdicStatus: fdicByDomain?.get(d.domain)?.primaryStatus ?? null,
        fdicAssetSize: fdicByDomain?.get(d.domain)?.primaryAssetSize ?? null,
        hasFdicIssue: fdicByDomain?.get(d.domain)?.hasFdicIssue ?? false,
        fdicCertCount: fdicByDomain?.get(d.domain)?.certs.length ?? 0,
      };
    });
  }, [domainStatuses, overrideMap, eventsByDomain, fdicByDomain, contactEmailMap]);

  // Build dynamic enum options for useRestriction (small set, used for quick filter chips)
  const useRestrictionEnumOptions = useMemo(() => {
    const values = new Set<string>();
    let hasBlank = false;
    for (const row of reportData) {
      const v = row.useRestrictionFilterValue;
      if (v)
        values.add(v);
      else hasBlank = true;
    }
    const options = [...values]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .slice(0, 30)
      .map((v) => ({ value: v, label: v }));

    // Include a blank/null option if any rows are blank.
    if (hasBlank) {
      options.unshift({ value: '', label: '(Blank)' });
    }

    return options;
  }, [reportData]);

  const filterFields = useMemo((): FilterField<MonitorDomainRow>[] => {
    return [
      ...BASE_MONITOR_FILTER_FIELDS,
      {
        field: 'useRestrictionNotePresent',
        label: 'Use Restriction Note',
        groupLabel: 'Use Restriction Note',
        type: 'enum',
        enumOptions: [
          { value: 'true', label: 'Present' },
          { value: 'false', label: 'Not Present' },
        ],
      },
      {
        field: 'useRestrictionFilterValue',
        label: 'Use Restriction',
        groupLabel: 'Use Restriction',
        type: 'enum',
        multiple: true,
        enumOptions: useRestrictionEnumOptions,
      },
      // Keep note text filter for ad-hoc searching
      {
        field: 'useRestrictionNote',
        label: 'Use Restriction Note (Text)',
        groupLabel: 'Use Restriction Note',
        type: 'string',
      },
    ];
  }, [useRestrictionEnumOptions]);

  const isLoading = !domainStatuses;

  return (
    <Container maxWidth={false} className="flex h-[calc(100vh-8rem)] flex-col pb-6">
      <ConvexReportBuilder
        data={reportData}
        isLoading={isLoading}
        columns={MONITOR_COLUMNS}
        filterFields={filterFields}
        presets={MONITOR_PRESETS}
        title="Monitor Report Builder"
        description="Build custom reports from domain monitor data. Use quick filters, advanced conditions, and export to Excel."
        defaultFilename="monitor_report"
        getRowId={(row) => row.domain}
        reportType="monitor"
      />
    </Container>
  );
}

export default ReportBuilderPage;
